import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_SCHEME,
  colorSchemeSchema,
  themeModeSchema,
  type ColorScheme,
  type ThemeMode,
} from '@avslack/shared';

/**
 * Tema es szinsema.
 *
 * A beallitas a FELHASZNALOHOZ tartozik (szerver oldalon tarolva), hogy
 * kovesse a gepek kozott. A `localStorage` viszont megmarad gyorsitotarnak:
 * ebbol allitjuk be a temat MEG AZ ELSO FESTES ELOTT, kulonben minden
 * betoltesnel felvillanna a vilagos hatter a sotet tema alatt.
 */

const MODE_KEY = 'avslack.theme';
const SCHEME_KEY = 'avslack.scheme';

function readCached<T>(key: string, parse: (value: string) => T, fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return stored ? parse(stored) : fallback;
  } catch {
    // Privat ablak vagy letiltott tarolas - az alapertelmezes a tartalek.
    return fallback;
  }
}

const readMode = (): ThemeMode =>
  readCached(MODE_KEY, (value) => themeModeSchema.catch('system').parse(value), 'system');

const readScheme = (): ColorScheme =>
  readCached(
    SCHEME_KEY,
    (value) => colorSchemeSchema.catch(DEFAULT_SCHEME).parse(value),
    DEFAULT_SCHEME,
  );

/**
 * A "system" eseten NEM allitunk `data-theme` attributumot: ilyenkor a CSS-ben
 * levo `prefers-color-scheme` szabaly dont. Igy egyetlen helyen van a temalogika.
 */
function apply(mode: ThemeMode, scheme: ColorScheme): void {
  const root = document.documentElement;

  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);

  root.setAttribute('data-scheme', scheme);
  syncThemeColor();
}

/**
 * Az ALLAPOTSOR / CIMSOR szine kovesse az ALKALMAZAS temajat (K2).
 *
 * Az index.html ket `theme-color`-ja csak a RENDSZER temajat koveti; aki az
 * appban sotetre valt, annak eddig vilagos sav maradt felul. A szint a
 * felulet tenyleges hatterebol vesszuk - egy keppontnyi vasznon at, mert a
 * kiszamolt ertek `oklch()` is lehet, amit nem minden bongeszo fogad el
 * `theme-color`-kent.
 */
function syncThemeColor(): void {
  const surface = getComputedStyle(document.body ?? document.documentElement).backgroundColor;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const context = canvas.getContext('2d');
  if (!context || !surface) return;
  context.fillStyle = surface;
  context.fillRect(0, 0, 1, 1);
  const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
  const hex = `#${[r, g, b].map((v) => (v ?? 0).toString(16).padStart(2, '0')).join('')}`;

  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) meta.remove();
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  meta.content = hex;
  document.head.appendChild(meta);
}

/** Rendszer-temanal a rendszer valtasat is kovetni kell. */
if (typeof window !== 'undefined') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', () => requestAnimationFrame(syncThemeColor));
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(readMode);
  const [scheme, setScheme] = useState<ColorScheme>(readScheme);

  useEffect(() => {
    apply(mode, scheme);
    try {
      localStorage.setItem(MODE_KEY, mode);
      localStorage.setItem(SCHEME_KEY, scheme);
    } catch {
      // A tarolas hianya nem torheti el a temavaltast.
    }
  }, [mode, scheme]);

  const cycle = useCallback(() => {
    setMode((current) =>
      current === 'system' ? 'light' : current === 'light' ? 'dark' : 'system',
    );
  }, []);

  return { mode, scheme, setMode, setScheme, cycle, preference: mode, setPreference: setMode };
}

/** Elso festes elott fut, hogy ne villanjon vilagos hatter sotet temanal. */
export function applyStoredThemeEarly(): void {
  apply(readMode(), readScheme());
}

/**
 * A szerverrol erkezo beallitas atvezetese.
 *
 * Masik gepen valtott temat a felhasznalo? Bejelentkezes utan ez allitja be.
 * Csak akkor ir, ha tenyleg mas - kulonben minden session-lekeres ujrafestene.
 */
export function syncThemeFromServer(mode: ThemeMode, scheme: ColorScheme): void {
  if (readMode() === mode && readScheme() === scheme) return;

  try {
    localStorage.setItem(MODE_KEY, mode);
    localStorage.setItem(SCHEME_KEY, scheme);
  } catch {
    // lasd fent
  }
  apply(mode, scheme);
}
